import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * The vendo registration wizard's own step vocabulary, lifted so adding a
 * MikroTik to ISP Business looks like adding a vendo (Ray, 2026-08-28).
 *
 * An operator who has registered a vendo has already learned this shape once —
 * numbered steps, a dark script block with a Copy beside it, and the next step
 * only opening when the previous one is done. Inventing a second shape for the
 * same job is work for them, not for us.
 */

export const StepRow = styled.div<{ $done?: boolean }>`
   display: flex;
   gap: 12px;
   padding: 14px 0;
   border-bottom: 1px solid ${colors.GRAY13};
   opacity: ${({ $done }) => ($done ? 0.65 : 1)};

   &:last-child {
      border-bottom: none;
   }
`;

export const StepBadge = styled.div<{ $active: boolean; $done?: boolean }>`
   flex-shrink: 0;
   width: 30px;
   height: 30px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 14px;
   font-weight: 800;
   border: 1px solid
      ${({ $active, $done }) =>
         $done ? colors.SUCCESS : $active ? colors.PRIMARY : colors.GRAY12};
   background: ${({ $active, $done }) =>
      $done ? colors.SUCCESS : $active ? colors.PRIMARY : 'transparent'};
   color: ${({ $active, $done }) =>
      $done || $active ? colors.WHITE : colors.GRAY3};
`;

export const StepBody = styled.div`
   flex: 1;
   min-width: 0;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

export const StepEyebrow = styled.span<{ $active: boolean }>`
   font-size: 10px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.1em;
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
`;

export const StepTitle = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   line-height: 1.3;
`;

export const StepNote = styled.p`
   margin: 0;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY5};
`;

export const ScriptRow = styled.div`
   display: flex;
   gap: 10px;
   align-items: center;
`;

export const ScriptLabel = styled.div`
   flex: 1;
   font-size: 12px;
   color: ${colors.GRAY1};
   line-height: 1.4;
`;

export const ScriptPre = styled.pre`
   margin: 0;
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 10px;
   line-height: 1.5;
   padding: 10px;
   background: ${colors.MAIN_DARK};
   color: ${colors.WHITE};
   border-radius: 8px;
   max-height: 190px;
   overflow: auto;
   white-space: pre;
`;

export const CopyBtn = styled.button`
   flex-shrink: 0;
   padding: 6px 14px;
   border-radius: 7px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
`;

/** The live "is it there yet" line under the script. */
export const WaitRow = styled.div<{ $on: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ $on }) => ($on ? colors.SUCCESS : colors.GRAY5)};
`;

export const Pulse = styled.span<{ $on: boolean }>`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background: ${({ $on }) => ($on ? colors.SUCCESS : colors.GRAY12)};
`;

/**
 * ─── One step at a time ───
 *
 * Ray, 2026-08-29: *"i dont like compacting it in 1 screen. i want similar to
 * adding vendo, a previous and next button in each step."*
 *
 * Stacking every step on one screen makes the operator scroll past work they
 * have already done to reach the thing they are on, and gives a router being
 * registered the same visual weight as the form that named it. The vendo wizard
 * pages instead — bullets across the top, one body, Back and Next — and an
 * operator who has registered a vendo has already learned that shape.
 */

export const Stepper = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
   padding: 4px 2px;
`;

export const Bullet = styled.div<{ $active: boolean; $done: boolean }>`
   width: 22px;
   height: 22px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 11px;
   font-weight: 700;
   flex-shrink: 0;
   ${({ $active, $done }) => {
      if ($done) return `background: ${colors.SUCCESS}; color: ${colors.WHITE};`;
      if ($active)
         return `background: ${colors.PRIMARY}; color: ${colors.WHITE}; box-shadow: 0 0 0 3px ${colors.PRIMARY}25;`;
      return `background: ${colors.GRAY12}; color: ${colors.GRAY5};`;
   }}
`;

export const Connector = styled.div<{ $done: boolean }>`
   flex: 1;
   height: 2px;
   background: ${({ $done }) => ($done ? colors.SUCCESS : colors.GRAY12)};
   transition: background 0.2s;
`;

export const StepCaption = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   text-align: center;
   margin: 0 0 4px;
   strong {
      color: ${colors.MAIN_DARK};
   }
`;

/** The one step on screen. A fixed floor stops the modal jumping between steps. */
export const StepPane = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   min-height: 250px;
`;

export const NavRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   margin-top: 14px;
   padding-top: 12px;
   border-top: 1px solid ${colors.GRAY13};
`;

export const NavBtns = styled.div`
   display: flex;
   gap: 8px;
   margin-left: auto;
`;

/**
 * The way out when the two-line fetch cannot run.
 *
 * Part 2 is fetched now rather than pasted whole, which removes the
 * ~1,812-byte Winbox wall and the silent truncation with it. But a router
 * behind a proxy or a blocked DNS cannot fetch at all, and an operator staring
 * at a fetch error needs an answer that is not "call support".
 */
export const FallbackLink = styled.button`
   display: block;
   margin-top: 8px;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   border: none;
   background: none;
   padding: 0;
   color: ${colors.GRAY5};
   text-decoration: underline;
   cursor: pointer;
`;
export const FallbackNote = styled.p`
   margin: 8px 0 6px;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY5};
`;
